type TypedField = { type?: string; value?: unknown }

function isUniDataTableEntry(entry: unknown): boolean {
  return Boolean(
    entry &&
      typeof entry === 'object' &&
      String((entry as TypedField).type ?? '') === 'UniDataTable',
  )
}

function readUniDataTableRows(entry: unknown): unknown[] {
  if (!entry || typeof entry !== 'object') return []
  const e = entry as { value?: { rows?: unknown[] }; rows?: unknown[] }
  if (Array.isArray(e.value?.rows)) return e.value!.rows!
  if (Array.isArray(e.rows)) return e.rows
  return []
}

function collectTableColumnNames(entry: unknown): Set<string> {
  const names = new Set<string>()
  const e = entry as { value?: { columns?: unknown } }
  const columns = e.value?.columns
  if (Array.isArray(columns)) {
    for (const col of columns) {
      const name = String(col ?? '').trim()
      if (name) names.add(name)
    }
  }
  for (const row of readUniDataTableRows(entry)) {
    if (!row || typeof row !== 'object') continue
    const fields = (row as { fields?: Record<string, unknown> }).fields
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      for (const key of Object.keys(fields)) {
        const name = String(key ?? '').trim()
        if (name) names.add(name)
      }
    }
  }
  return names
}

/**
 * Слот одного элемента в forms registry: только поля этого элемента.
 * Если в слоте есть UniDataTable, одноимённые скаляры — это flatten строки (onChange / selectedData),
 * а не getData элемента. SwingTable/SwingTiles.getData для родителя формы отдаёт одно поле-таблицу.
 *
 * Важно делать это **по слоту**, а не по всему form UniData: иначе колонка `_id` в Tiles
 * вырежет identity документа из Identificators (WUI-507 bleed).
 */
export function omitLeakedTableColumnFieldsFromElementSlot(
  slot: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!slot || typeof slot !== 'object' || Array.isArray(slot)) {
    return slot ?? {}
  }

  const tableFieldNames = new Set<string>()
  const columnNames = new Set<string>()
  for (const [key, value] of Object.entries(slot)) {
    if (!isUniDataTableEntry(value)) continue
    tableFieldNames.add(key)
    for (const col of collectTableColumnNames(value)) {
      if (col !== key) columnNames.add(col)
    }
  }
  if (columnNames.size === 0) return slot

  const out: Record<string, unknown> = { ...slot }
  for (const col of columnNames) {
    if (tableFieldNames.has(col)) continue
    if (!Object.prototype.hasOwnProperty.call(out, col)) continue
    if (isUniDataTableEntry(out[col])) continue
    delete out[col]
  }
  return out
}
